import { useCallback, useEffect, useMemo, useState } from "react";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import { getArchivedCandidates, getCandidatesStatuses } from "../services/candidateApi";
import { getVacancies } from "../services/vacancyApi";
import CandidateList from "../components/candidates/CandidateList";
import CandidateDetails from "../components/candidates/CandidateDetails";

const PAGE_SIZE = 20;

export default function ArchiveCandidatesPage() {
  const [candidates, setCandidates] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { showAlert } = useAlertContext();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [vacancyFilter, setVacancyFilter] = useState("");
  const [vacancies, setVacancies] = useState([]);
  const [statusDict, setStatusDict] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const fetchArchived = useCallback(async ({ signal } = {}) => {
    try {
      setRefreshing(true);
      const data = await getArchivedCandidates({
        page,
        perPage: PAGE_SIZE,
        search: debouncedSearch,
        status: statusFilter,
        vacancyId: vacancyFilter,
        signal,
      });
      if (signal?.aborted) return;
      const list = Array.isArray(data) ? data : (data?.items ?? []);
      const responseTotal = Number(data?.total ?? list.length);
      const responseTotalPages = Number(data?.total_pages ?? (responseTotal ? 1 : 0));
      if (responseTotalPages > 0 && page > responseTotalPages) {
        setPage(responseTotalPages);
        return;
      }
      setCandidates(list.map((candidate) => ({
        ...candidate,
        status: candidate.current_status ?? candidate.status ?? "не подходит: резюме",
        status_date: candidate.current_status_date ?? candidate.status_date ?? null,
        vacancy_id: candidate.last_vacancy_id ?? candidate.vacancy_id ?? null,
        vacancy_name: candidate.last_vacancy_title ?? candidate.vacancy_name ?? null,
      })));
      setTotal(responseTotal);
      setTotalPages(responseTotalPages);
      setInitialLoading(false);
    } catch (e) {
      if (e?.name === "AbortError" || signal?.aborted) return;
      console.error(e);
      showAlert(`Ошибка загрузки архива: ${e?.response?.data?.detail ?? e.message}`, "error");
    } finally {
      if (!signal?.aborted) {
        setRefreshing(false);
        setInitialLoading(false);
      }
    }
  }, [debouncedSearch, page, showAlert, statusFilter, vacancyFilter]);

  useEffect(() => {
    const controller = new AbortController();
    fetchArchived({ signal: controller.signal });
    return () => controller.abort();
  }, [fetchArchived]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(searchQuery.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getVacancies();
        setVacancies(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Не удалось загрузить вакансии", e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await getCandidatesStatuses();
        setStatusDict(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Не удалось загрузить статусы", e);
        setStatusDict([]);
      }
    })();
  }, []);

  const visiblePages = useMemo(() => {
    const first = Math.max(1, page - 2);
    const last = Math.min(totalPages, page + 2);
    return Array.from({ length: Math.max(0, last - first + 1) }, (_, index) => first + index);
  }, [page, totalPages]);

  const handleChildChanged = () => {
    fetchArchived();
  };

  if (initialLoading) {
    return (
      <MainLayout>
        <p className="text-[#666666]">Загрузка…</p>
      </MainLayout>
    );
  }

    return (
      <MainLayout className="flex flex-col h-full overflow-y-auto">
        <div className="mb-6 space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">Архив кандидатов</h1>
              <p className="mt-1 text-sm text-slate-500">Кандидаты, по которым работа завершена.</p>
            </div>
          </div>
  
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200/60 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Поиск</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="ФИО, телефон..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
              </div>
            </div>
  
            <div className="w-full sm:w-48">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Статус</label>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all appearance-none cursor-pointer"
                >
                  <option value="">Все статусы</option>
                  {statusDict.map(s => (
                    <option key={s.id} value={s.code ?? s.name}>{s.name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>
            </div>
  
            <div className="w-full sm:w-48">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Вакансия</label>
              <div className="relative">
                <select
                  value={vacancyFilter}
                  onChange={(e) => { setVacancyFilter(e.target.value); setPage(1); }}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all appearance-none cursor-pointer"
                >
                  <option value="">Все вакансии</option>
                  {vacancies.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>
            </div>
          </div>
  
        <div className="flex items-center justify-between text-sm text-slate-500" aria-live="polite">
          <span className="flex items-center gap-2">
            Найдено кандидатов: {total}
            {refreshing ? <span className="inline-flex items-center gap-1.5 text-[#9b7c13]" data-testid="archive-refreshing"><span className="h-3 w-3 animate-spin rounded-full border-2 border-[#cda834]/30 border-t-[#cda834]" />Обновляем…</span> : null}
          </span>
          {totalPages > 0 ? <span>Страница {page} из {totalPages}</span> : null}
        </div>

        {candidates.length === 0 ? (
            <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200/60 text-center flex flex-col items-center">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                </div>
                <h3 className="text-lg font-medium text-slate-800">Ничего не найдено</h3>
                <p className="text-slate-500 mt-1">Попробуйте изменить параметры поиска или фильтры.</p>
            </div>
        ) : (
            <div className={`transition-opacity ${refreshing ? "opacity-60" : "opacity-100"}`} aria-busy={refreshing}>
            <CandidateList
                candidates={candidates}
                onCandidateEdit={() => {}}
                onCandidateDelete={() => {}}
                onCandidateClick={setSelectedCandidate}
                onChange={handleChildChanged}
                isArchiveView={true}
            />
            </div>
        )}

        {totalPages > 1 ? (
          <nav className="flex flex-wrap items-center justify-center gap-2" aria-label="Страницы архива кандидатов">
            <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))}
              disabled={page <= 1 || refreshing}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium disabled:opacity-40">
              Назад
            </button>
            {visiblePages.map((pageNumber) => (
              <button key={pageNumber} type="button" onClick={() => setPage(pageNumber)} disabled={refreshing}
                aria-current={pageNumber === page ? "page" : undefined}
                className={`min-w-10 rounded-lg border px-3 py-2 text-sm font-semibold ${pageNumber === page ? "border-[#cda834] bg-[#cda834] text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                {pageNumber}
              </button>
            ))}
            <button type="button" onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              disabled={page >= totalPages || refreshing}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium disabled:opacity-40">
              Вперёд
            </button>
          </nav>
        ) : null}
        </div>
  
        {selectedCandidate && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6" onClick={() => setSelectedCandidate(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100">
                <h2 className="text-xl font-bold text-slate-900">Детали кандидата</h2>
                <button onClick={() => setSelectedCandidate(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                <CandidateDetails
                  candidate={selectedCandidate}
                  isArchiveView={true}
                  onChange={handleChildChanged}
                />
              </div>
            </div>
          </div>
        )}
      </MainLayout>
    );
  }
